import type { StateCreator } from "zustand";
import {
  expenseApi,
  type ExpenseCategoryCreatePayload,
  type ExpenseCategoryUpdatePayload,
  type ExpenseDateRange,
  type ExpenseListParams,
  type PaymentAccountCreatePayload,
  type PaymentAccountUpdatePayload,
} from "../../api/expense/api";
import type {
  Expense,
  ExpenseCategory,
  ExpenseCategoryReport,
  ExpenseMerchantDetails,
  ExpensePaymentAccountReport,
  PaymentAccount,
} from "../../expense/types";
import { createLatestRequest, isAbortError } from "./requests";

const categoryReportRequest = createLatestRequest();
const paymentAccountReportRequest = createLatestRequest();
const referencesRequest = createLatestRequest();
const merchantRequest = createLatestRequest();
const merchantMutationRequest = createLatestRequest();

export const resetExpenseReferenceRequests = () => {
  categoryReportRequest.reset();
  paymentAccountReportRequest.reset();
  referencesRequest.reset();
  merchantRequest.reset();
  merchantMutationRequest.reset();
};

export type ExpenseReferenceSlice = {
  projectVersion: number;
  referencesHydrated: boolean;
  referenceLoading: boolean;
  referenceActionId: string | null;
  categoryLoadingId: string | null;
  paymentAccountLoadingId: string | null;
  selectedCategory: ExpenseCategory | null;
  selectedPaymentAccount: PaymentAccount | null;
  selectedMerchant: ExpenseMerchantDetails | null;
  merchantLoadingKey: string | null;
  merchantError: string | null;
  merchantVersion: number;
  loadMerchant: (key: string, force?: boolean) => Promise<void>;
  categoryReport: ExpenseCategoryReport[];
  categoryReportKey: string;
  categoryReportLoading: boolean;
  categoryReportError: string | null;
  paymentAccountReport: ExpensePaymentAccountReport[];
  paymentAccountReportKey: string;
  paymentAccountReportLoading: boolean;
  paymentAccountReportError: string | null;
  loadReferences: (force?: boolean) => Promise<void>;
  loadCategory: (id: string) => Promise<ExpenseCategory>;
  loadPaymentAccount: (id: string) => Promise<PaymentAccount>;
  loadCategoryReport: (
    range: ExpenseDateRange,
    force?: boolean,
  ) => Promise<void>;
  loadPaymentAccountReport: (
    range: ExpenseDateRange,
    force?: boolean,
  ) => Promise<void>;
  createCategory: (
    payload: ExpenseCategoryCreatePayload,
  ) => Promise<ExpenseCategory>;
  updateCategory: (
    id: string,
    payload: ExpenseCategoryUpdatePayload,
  ) => Promise<ExpenseCategory>;
  createPaymentAccount: (
    payload: PaymentAccountCreatePayload,
  ) => Promise<PaymentAccount>;
  updatePaymentAccount: (
    id: string,
    payload: PaymentAccountUpdatePayload,
  ) => Promise<PaymentAccount>;
  applyReferenceChanged: (
    referenceType: "category" | "payment_account" | "merchant" | "project",
    referenceId?: string,
  ) => Promise<void>;
  updateMerchant: (
    key: string,
    payload: Pick<ExpenseMerchantDetails, "merchantAddress" | "storeNumber">,
  ) => Promise<ExpenseMerchantDetails>;
};

type ExpenseReferenceStore = ExpenseReferenceSlice & {
  selectedExpense: Expense | null;
  hydrated: boolean;
  activeParams: ExpenseListParams;
  load: (params?: ExpenseListParams, force?: boolean) => Promise<void>;
  loadDetail: (id: string, options?: { silent?: boolean }) => Promise<void>;
  categories: ExpenseCategory[];
  paymentAccounts: PaymentAccount[];
  actionError: string | null;
  expenseQueryVersion: number;
};

export const expenseReferenceInitialState = {
  projectVersion: 0,
  referencesHydrated: false,
  referenceLoading: false,
  referenceActionId: null,
  categoryLoadingId: null,
  paymentAccountLoadingId: null,
  selectedCategory: null,
  selectedPaymentAccount: null,
  selectedMerchant: null,
  merchantLoadingKey: null,
  merchantError: null,
  merchantVersion: -1,
  categoryReport: [],
  categoryReportKey: "",
  categoryReportLoading: false,
  categoryReportError: null,
  paymentAccountReport: [],
  paymentAccountReportKey: "",
  paymentAccountReportLoading: false,
  paymentAccountReportError: null,
} satisfies Pick<
  ExpenseReferenceSlice,
  | "projectVersion"
  | "referencesHydrated"
  | "referenceLoading"
  | "referenceActionId"
  | "categoryLoadingId"
  | "paymentAccountLoadingId"
  | "selectedCategory"
  | "selectedPaymentAccount"
  | "selectedMerchant"
  | "merchantLoadingKey"
  | "merchantError"
  | "merchantVersion"
  | "categoryReport"
  | "categoryReportKey"
  | "categoryReportLoading"
  | "categoryReportError"
  | "paymentAccountReport"
  | "paymentAccountReportKey"
  | "paymentAccountReportLoading"
  | "paymentAccountReportError"
>;

export const createExpenseReferenceSlice: StateCreator<
  ExpenseReferenceStore,
  [],
  [],
  ExpenseReferenceSlice
> = (set, get) => ({
  ...expenseReferenceInitialState,
  loadReferences: async (force = false) => {
    if (get().referencesHydrated && !force) return;
    const request = referencesRequest.begin();
    set({ referenceLoading: true, actionError: null });
    try {
      const [categories, paymentAccounts] = await Promise.all([
        expenseApi.categories(true, request.signal),
        expenseApi.paymentAccounts(true, request.signal),
      ]);
      if (request.isCurrent())
        set((state) => ({ categories, paymentAccounts, referencesHydrated: true, projectVersion: state.projectVersion + 1 }));
    } catch (error) {
      if (!request.isCurrent() || isAbortError(error)) return;
      set({
        actionError: errorMessage(error, "Unable to load expense settings"),
      });
      throw error;
    } finally {
      if (request.isCurrent()) set({ referenceLoading: false });
      request.finish();
    }
  },
  loadCategory: async (id) => {
    const existing = get().categories.find((category) => category.id === id);
    if (existing) {
      set({ selectedCategory: existing });
      return existing;
    }
    set({ categoryLoadingId: id, actionError: null });
    try {
      const category = await expenseApi.category(id);
      if (get().categoryLoadingId !== id) return category;
      set((state) => ({
        selectedCategory: category,
        categories: replaceById(state.categories, category),
      }));
      return category;
    } catch (error) {
      if (get().categoryLoadingId === id)
        set({ actionError: errorMessage(error, "Unable to load category") });
      throw error;
    } finally {
      if (get().categoryLoadingId === id) set({ categoryLoadingId: null });
    }
  },
  loadPaymentAccount: async (id) => {
    const existing = get().paymentAccounts.find((account) => account.id === id);
    if (existing) {
      set({ selectedPaymentAccount: existing });
      return existing;
    }
    set({ paymentAccountLoadingId: id, actionError: null });
    try {
      const account = await expenseApi.paymentAccount(id);
      if (get().paymentAccountLoadingId !== id) return account;
      set((state) => ({
        selectedPaymentAccount: account,
        paymentAccounts: replaceById(state.paymentAccounts, account),
      }));
      return account;
    } catch (error) {
      if (get().paymentAccountLoadingId === id)
        set({
          actionError: errorMessage(error, "Unable to load payment account"),
        });
      throw error;
    } finally {
      if (get().paymentAccountLoadingId === id)
        set({ paymentAccountLoadingId: null });
    }
  },
  loadCategoryReport: async (range, force = false) => {
    const key = rangeKey(range);
    if (get().categoryReportKey === key && !force) return;
    const request = categoryReportRequest.begin();
    set({ categoryReportLoading: true, categoryReportError: null });
    try {
      const report = await expenseApi.reportByCategory(
        { ...range, includeComparison: true },
        request.signal,
      );
      if (request.isCurrent())
        set({ categoryReport: report, categoryReportKey: key });
    } catch (error) {
      if (!request.isCurrent() || isAbortError(error)) return;
      set({
        categoryReportError: errorMessage(
          error,
          "Unable to load category activity",
        ),
      });
      throw error;
    } finally {
      if (request.isCurrent()) set({ categoryReportLoading: false });
      request.finish();
    }
  },
  loadPaymentAccountReport: async (range, force = false) => {
    const key = rangeKey(range);
    if (get().paymentAccountReportKey === key && !force) return;
    const request = paymentAccountReportRequest.begin();
    set({
      paymentAccountReportLoading: true,
      paymentAccountReportError: null,
    });
    try {
      const report = await expenseApi.reportByPaymentAccount(
        { ...range, includeComparison: true },
        request.signal,
      );
      if (request.isCurrent())
        set({
          paymentAccountReport: report,
          paymentAccountReportKey: key,
        });
    } catch (error) {
      if (!request.isCurrent() || isAbortError(error)) return;
      set({
        paymentAccountReportError: errorMessage(
          error,
          "Unable to load payment account activity",
        ),
      });
      throw error;
    } finally {
      if (request.isCurrent()) set({ paymentAccountReportLoading: false });
      request.finish();
    }
  },
  createCategory: async (payload) => {
    set({ referenceActionId: "category:new", actionError: null });
    try {
      const category = await expenseApi.createCategory(payload);
      set((state) => ({
        categories: replaceById(state.categories, category),
        expenseQueryVersion: state.expenseQueryVersion + 1,
      }));
      return category;
    } catch (error) {
      set({ actionError: errorMessage(error, "Unable to create category") });
      throw error;
    } finally {
      set({ referenceActionId: null });
    }
  },
  updateCategory: async (id, payload) => {
    set({ referenceActionId: `category:${id}`, actionError: null });
    try {
      const category = await expenseApi.updateCategory(id, payload);
      set((state) => ({
          categories: replaceById(state.categories, category),
          expenseQueryVersion: state.expenseQueryVersion + 1,
          categoryReport: state.categoryReport.map((row) =>
            row.categoryId === id
              ? { ...row, categoryName: category.name }
              : row,
          ),
          selectedCategory:
            state.selectedCategory?.id === id
              ? category
              : state.selectedCategory,
      }));
      return category;
    } catch (error) {
      set({ actionError: errorMessage(error, "Unable to update category") });
      throw error;
    } finally {
      set({ referenceActionId: null });
    }
  },
  createPaymentAccount: async (payload) => {
    set({ referenceActionId: "payment_account:new", actionError: null });
    try {
      const account = await expenseApi.createPaymentAccount(payload);
      set((state) => ({
        paymentAccounts: replaceById(state.paymentAccounts, account),
        expenseQueryVersion: state.expenseQueryVersion + 1,
      }));
      return account;
    } catch (error) {
      set({
        actionError: errorMessage(error, "Unable to create payment account"),
      });
      throw error;
    } finally {
      set({ referenceActionId: null });
    }
  },
  updatePaymentAccount: async (id, payload) => {
    set({ referenceActionId: `payment_account:${id}`, actionError: null });
    try {
      const account = await expenseApi.updatePaymentAccount(id, payload);
      set((state) => ({
          paymentAccounts: replaceById(state.paymentAccounts, account),
          expenseQueryVersion: state.expenseQueryVersion + 1,
          paymentAccountReport: state.paymentAccountReport.map((row) =>
            row.paymentAccountId === id
              ? { ...row, paymentAccountLabel: account.label }
              : row,
          ),
          selectedPaymentAccount:
            state.selectedPaymentAccount?.id === id
              ? account
              : state.selectedPaymentAccount,
      }));
      return account;
    } catch (error) {
      set({
        actionError: errorMessage(error, "Unable to update payment account"),
      });
      throw error;
    } finally {
      set({ referenceActionId: null });
    }
  },
  loadMerchant: async (key, force = false) => {
    const state = get();
    if (!force && state.selectedMerchant?.merchantKey === key && state.merchantVersion === state.expenseQueryVersion) return;
    const request = merchantRequest.begin();
    const version = state.expenseQueryVersion;
    set({ merchantLoadingKey: key, merchantError: null,
      selectedMerchant: state.selectedMerchant?.merchantKey === key ? state.selectedMerchant : null });
    try {
      const merchant = await expenseApi.merchantDetail(key, request.signal);
      if (request.isCurrent()) set({ selectedMerchant: merchant, merchantVersion: version });
    } catch (error) {
      if (!request.isCurrent() || isAbortError(error)) return;
      set({ merchantError: errorMessage(error, "Unable to load merchant address") });
      throw error;
    } finally {
      if (request.isCurrent()) set({ merchantLoadingKey: null });
      request.finish();
    }
  },
  updateMerchant: async (key, payload) => {
    const mutation = merchantMutationRequest.begin();
    set({ referenceActionId: `merchant:${key}`, actionError: null });
    try {
      const merchant = await expenseApi.updateMerchant(key, payload);
      if (!mutation.isCurrent()) return merchant;
      // Keep the save response authoritative and avoid an immediate detail refetch.
      const isSelected = get().selectedMerchant?.merchantKey === key || get().merchantLoadingKey === key;
      if (isSelected) merchantRequest.reset();
      set((state) => ({
        expenseQueryVersion: state.expenseQueryVersion + 1,
        ...(isSelected ? {
          selectedMerchant: merchant, merchantVersion: state.expenseQueryVersion + 1,
          merchantLoadingKey: null, merchantError: null,
        } : {}),
      }));
      return merchant;
    } catch (error) {
      if (mutation.isCurrent()) set({ actionError: errorMessage(error, "Unable to update merchant") });
      throw error;
    } finally {
      if (mutation.isCurrent()) set({ referenceActionId: null });
      mutation.finish();
    }
  },
  applyReferenceChanged: async (referenceType, referenceId) => {
    if (referenceType !== "merchant" && referenceType !== "project") await get().loadReferences(true).catch(() => undefined);
    if (referenceType === "project") {
      const state = get();
      set({ projectVersion: state.projectVersion + 1, expenseQueryVersion: state.expenseQueryVersion + 1 });
      await Promise.all([
        ...(state.hydrated ? [state.load(state.activeParams, true)] : []),
        ...(state.selectedExpense && state.selectedExpense.project?.id === referenceId ? [state.loadDetail(state.selectedExpense.id, { silent: true })] : []),
      ]);
      return;
    }
    const state = get();
    if (referenceType === "category" && state.categoryReportKey) {
      await state
        .loadCategoryReport(rangeFromKey(state.categoryReportKey), true)
        .catch(() => undefined);
    }
    if (
      referenceType === "payment_account" &&
      state.paymentAccountReportKey
    ) {
      await state
        .loadPaymentAccountReport(
          rangeFromKey(state.paymentAccountReportKey),
          true,
        )
        .catch(() => undefined);
    }
    if (
      referenceType === "category" &&
      referenceId &&
      get().selectedCategory?.id === referenceId
    ) {
      await get().loadCategory(referenceId).catch(() => undefined);
    }
    if (
      referenceType === "payment_account" &&
      referenceId &&
      get().selectedPaymentAccount?.id === referenceId
    ) {
      await get().loadPaymentAccount(referenceId).catch(() => undefined);
    }
    set((current) => ({
      expenseQueryVersion: current.expenseQueryVersion + 1,
    }));
  },
});

const replaceById = <T extends { id: string }>(items: T[], item: T) =>
  items.some((current) => current.id === item.id)
    ? items.map((current) => (current.id === item.id ? item : current))
    : [...items, item];

const rangeKey = (range: ExpenseDateRange) =>
  `${range.startDate || ""}|${range.endDate || ""}`;

const rangeFromKey = (key: string): ExpenseDateRange => {
  const [startDate, endDate] = key.split("|");
  return {
    ...(startDate ? { startDate } : {}),
    ...(endDate ? { endDate } : {}),
  };
};

const errorMessage = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback;
